import { compareAsc, isBefore, isEqual, set } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { OrderCurrencyExchangeSourceCurrency, OrderCurrencySourceCurrencyWithBalance } from '../../resources';
import { ExchangePeriodHoursType } from '../../types';
import { BudapestTimezone, isDefined, Loadable, LoadableState, parseExchangeDate, useToday } from '../../utils';

const RefreshIntervalInMilliseconds = 5000;

export type UseRefreshingExchangeCurrenciesParams<Item, Input, ValidatedInput extends Input> = {
  load: (input: ValidatedInput) => Promise<Item[]>;
  isInputValid: (input: Input) => input is ValidatedInput;
  getRefreshTimes: (item: Item) => Date[];
  input: Input;
  exchangePeriodHours: ExchangePeriodHoursType;
};

export type UseRefreshingExchangeCurrenciesOutput<Item> = {
  data: LoadableState<Item[]>;
  refresh: () => Promise<void>;
  reset: () => void;
  startRefreshing: () => void;
  stopRefreshing: () => void;
};

export const useRefreshingExchangeCurrencies = <Item, Input, ValidatedInput extends Input>({
  load,
  getRefreshTimes,
  isInputValid,
  input,
  exchangePeriodHours,
}: UseRefreshingExchangeCurrenciesParams<Item, Input, ValidatedInput>): UseRefreshingExchangeCurrenciesOutput<Item> => {
  const today = useToday();
  const [data, setData] = useState<LoadableState<Item[]>>(Loadable.uninitialized);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const requestIdRef = useRef(0);
  const lastRefreshCheckRef = useRef<Date>(new Date(0));

  const fixedRefreshTimes = useMemo<Date[]>(() => {
    const budapestToday = toZonedTime(today, BudapestTimezone);
    const { afternoon, morning, night } = exchangePeriodHours;
    return [afternoon, morning, night].filter(isDefined).map(([hours, minutes]) =>
      fromZonedTime(
        set(budapestToday, {
          hours,
          minutes,
          seconds: 0,
          milliseconds: 0,
        }),
        BudapestTimezone,
      ),
    );
  }, [exchangePeriodHours, today]);

  const refreshTimes = useMemo<Date[]>(() => {
    const refreshTimesFromData = Loadable.hasValue(data) ? data.flatMap(getRefreshTimes) : [];
    const allRefreshTimes = [...fixedRefreshTimes, ...refreshTimesFromData];
    const uniqueRefreshTimes = new Map(
      allRefreshTimes.map(refreshTime => [refreshTime.getTime(), refreshTime]),
    ).values();
    return Array.from(uniqueRefreshTimes).sort(compareAsc);
  }, [data, fixedRefreshTimes, getRefreshTimes]);

  const updateCurrencies = useCallback((requestId: number, value: LoadableState<Item[]>) => {
    if (requestId !== requestIdRef.current) {
      return;
    }
    setData(value);
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    const requestId = ++requestIdRef.current;

    if (!isInputValid(input)) {
      updateCurrencies(requestId, Loadable.uninitialized);
      return;
    }

    updateCurrencies(requestId, Loadable.loading);
    try {
      const newCurrencies = await load(input);
      updateCurrencies(requestId, Loadable.loaded(newCurrencies));
    } catch {
      updateCurrencies(requestId, Loadable.failed);
    }
  }, [input, isInputValid, load, updateCurrencies]);

  const handleRefreshTick = useEffectEvent((): void => {
    const now = new Date();

    if (
      refreshTimes.some(
        refreshTime =>
          (isBefore(refreshTime, now) || isEqual(refreshTime, now)) &&
          isBefore(lastRefreshCheckRef.current, refreshTime),
      )
    ) {
      void refresh();
    }

    lastRefreshCheckRef.current = now;
  });

  useEffect(() => {
    if (!isRefreshing) {
      return;
    }

    lastRefreshCheckRef.current = new Date();
    const intervalId = setInterval((): void => {
      handleRefreshTick();
    }, RefreshIntervalInMilliseconds);

    return () => {
      clearInterval(intervalId);
    };
  }, [isRefreshing]);

  const startRefreshing = useCallback((): void => {
    setIsRefreshing(true);
  }, []);

  const stopRefreshing = useCallback((): void => {
    setIsRefreshing(false);
  }, []);

  const reset = useCallback((): void => {
    setData(Loadable.uninitialized);
    lastRefreshCheckRef.current = new Date(0);
    requestIdRef.current = 0;
  }, []);

  return {
    data,
    reset,
    refresh,
    startRefreshing,
    stopRefreshing,
  };
};

export const defaultGetRefreshTimes = (
  item: OrderCurrencyExchangeSourceCurrency | OrderCurrencySourceCurrencyWithBalance,
): Date[] => {
  return [item.exchangePeriodFrom, item.exchangePeriodTo].filter(isDefined).map(parseExchangeDate);
};
